import React, { useState } from 'react';
import { backdropProps } from '../utils/backdropClose';
import { supabase } from '../supabase';

// Add/Edit lookup option — single modal, AddConsultantModal isEdit pattern.
// Props: isDark; option (null=add / row=edit); listId; defaultSortOrder; onClose; onSuccess(row).
export default function AddOptionModal({ isDark, option, listId, defaultSortOrder, onClose, onSuccess }) {
  const isEdit = !!option;
  const [form, setForm] = useState({
    label:       option?.label || '',
    code:        option?.code || '',
    description: option?.description || '',
    sort_order:  option?.sort_order ?? (defaultSortOrder ?? ''),
    active:      option ? option.active !== false : true,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const c = isDark
    ? { card: '#2F3854', border: '#374060', text: '#e8eaf2', muted: 'var(--slate)', input: 'var(--ink)', inputBorder: '#434C68' }
    : { card: '#ffffff', border: 'var(--line)', text: 'var(--ink)', muted: 'var(--slate)', input: '#ffffff', inputBorder: '#c8ccd8' };
  const s = {
    label:    { display: 'block', fontSize: '11px', fontWeight: '700', color: c.muted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px' },
    input:    { width: '100%', padding: '8px 11px', background: c.input, border: `1px solid ${c.inputBorder}`, borderRadius: '7px', color: c.text, fontSize: '13px', outline: 'none', boxSizing: 'border-box', fontFamily: "'Public Sans',system-ui,sans-serif" },
    textarea: { width: '100%', padding: '8px 11px', background: c.input, border: `1px solid ${c.inputBorder}`, borderRadius: '7px', color: c.text, fontSize: '13px', outline: 'none', boxSizing: 'border-box', fontFamily: "'Public Sans',system-ui,sans-serif", minHeight: '52px', resize: 'vertical' },
    btn:      { padding: '9px 18px', background: 'linear-gradient(135deg,#B40000,#9C0000)', border: 'none', borderRadius: '7px', color: 'white', fontSize: '13px', fontWeight: '700', cursor: 'pointer', fontFamily: "'Public Sans',system-ui,sans-serif" },
    btnGhost: { padding: '9px 16px', background: 'transparent', border: `1px solid ${c.border}`, borderRadius: '7px', color: c.muted, fontSize: '13px', cursor: 'pointer', fontFamily: "'Public Sans',system-ui,sans-serif" },
  };

  const set = (f, v) => setForm(prev => ({ ...prev, [f]: v }));
  const txt = v => (v && v.trim() ? v.trim() : null);
  const num = v => (v === '' || v == null || Number.isNaN(Number(v)) ? null : Math.trunc(Number(v)));

  async function handleSave() {
    if (!form.label.trim()) { setError('Label is required.'); return; }
    setSaving(true); setError('');
    try {
      const fields = { label: form.label.trim(), code: txt(form.code), description: txt(form.description), sort_order: num(form.sort_order), active: !!form.active };
      const q = isEdit
        ? supabase.from('lookup_options').update(fields).eq('id', option.id).select().single()
        : supabase.from('lookup_options').insert({ ...fields, list_id: listId }).select().single();
      const { data, error: e } = await q;
      if (e) throw e;
      onSuccess(data);
    } catch (e) {
      setError(e?.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div {...backdropProps(onClose)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: c.card, borderRadius: '14px', padding: '28px', maxWidth: '500px', width: '100%', boxShadow: '0 8px 40px rgba(0,0,0,0.3)', fontFamily: "'Public Sans',system-ui,sans-serif" }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
          <div style={{ fontFamily: "'Archivo',sans-serif", fontSize: '20px', fontWeight: '700', color: c.text }}>{isEdit ? 'Edit Option' : 'Add Option'}</div>
          <button onClick={onClose} type="button" style={{ background: 'none', border: 'none', color: c.muted, fontSize: '18px', cursor: 'pointer', padding: '0 4px', lineHeight: 1 }}>✕</button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '10px', marginBottom: '14px' }}>
          <div><label style={s.label}>Label *</label><input style={s.input} value={form.label} onChange={e => set('label', e.target.value)} placeholder="e.g. RE Appeals" /></div>
          <div><label style={s.label}>Code</label><input style={s.input} value={form.code} onChange={e => set('code', e.target.value)} placeholder="e.g. re_appeals" /></div>
        </div>
        <div style={{ marginBottom: '14px' }}><label style={s.label}>Description</label><textarea style={s.textarea} value={form.description} onChange={e => set('description', e.target.value)} /></div>
        <div style={{ display: 'flex', alignItems: 'end', gap: '16px', marginBottom: '14px' }}>
          <div style={{ width: '120px' }}><label style={s.label}>Sort Order</label><input type="number" style={s.input} value={form.sort_order} onChange={e => set('sort_order', e.target.value)} /></div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: c.text, cursor: 'pointer', paddingBottom: '8px' }}>
            <input type="checkbox" checked={form.active} onChange={e => set('active', e.target.checked)} style={{ cursor: 'pointer' }} />
            Active
          </label>
        </div>
        {error && <div style={{ color: '#f87171', fontSize: '12px', marginBottom: '10px' }}>{error}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
          <button type="button" onClick={onClose} disabled={saving} style={s.btnGhost}>Cancel</button>
          <button type="button" onClick={handleSave} disabled={saving} style={s.btn}>{saving ? 'Saving…' : (isEdit ? 'Save Changes' : 'Add Option')}</button>
        </div>
      </div>
    </div>
  );
}
